import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { questionnairesAPI } from '../utils/api';
import toastr from 'toastr';

const QuestionnaireResponse = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [questionnaire, setQuestionnaire] = useState(null);
  const [response, setResponse] = useState(null);
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const loadQuestionnaire = useCallback(async () => {
    try {
      const result = await questionnairesAPI.getResponseByToken(token);
      setQuestionnaire(result.data.questionnaire);
      setResponse(result.data.response);
      
      if (result.data.response.responses && result.data.response.responses.length > 0) {
        const existingAnswers = {};
        result.data.response.responses.forEach(resp => {
          existingAnswers[resp.questionId] = resp.answer;
        });
        setAnswers(existingAnswers);
      }
    } catch (error) {
      console.error('Failed to load questionnaire:', error);
      if (error.response?.status === 410) {
        toastr.error('This questionnaire has expired');
      } else {
        toastr.error('Failed to load questionnaire. Please check the link.');
      }
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    loadQuestionnaire();
  }, [loadQuestionnaire]);

  const handleAnswerChange = (questionId, value) => {
    setAnswers({ ...answers, [questionId]: value });
  };

  const handleOptionChange = (questionId, optionValue, checked) => {
    const currentAnswer = answers[questionId] || [];
    if (checked) {
      handleAnswerChange(questionId, [...currentAnswer, optionValue]);
    } else {
      handleAnswerChange(questionId, currentAnswer.filter(v => v !== optionValue));
    }
  };

  const handleSubmit = async () => {
    // Validate required questions
    for (const question of questionnaire.questions) {
      if (question.required) {
        const answer = answers[question._id?.toString() || questionnaire.questions.indexOf(question).toString()];
        if (!answer || (Array.isArray(answer) && answer.length === 0)) {
          toastr.error(`Please answer the required question: ${question.question}`);
          return;
        }
      }
    }

    setSubmitting(true);
    try {
      const responses = questionnaire.questions.map((q, index) => {
        const questionId = q._id?.toString() || index.toString();
        return {
          questionId,
          answer: answers[questionId] || ''
        };
      });

      await questionnairesAPI.submitResponse(token, responses);
      toastr.success('Questionnaire submitted successfully! Thank you for your time.');
      setTimeout(() => {
        navigate('/');
      }, 2000);
    } catch (error) {
      console.error('Failed to submit questionnaire:', error);
      toastr.error(error.response?.data?.error || 'Failed to submit questionnaire');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div style={{ 
        minHeight: '100vh', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        background: '#F9FAFB'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '1.125rem', color: '#6B7280' }}>Loading questionnaire...</div>
        </div>
      </div>
    );
  }

  if (!questionnaire) {
    return (
      <div style={{ 
        minHeight: '100vh', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        background: '#F9FAFB'
      }}>
        <div style={{ textAlign: 'center', maxWidth: '500px', padding: '2rem' }}>
          <h1 style={{ fontSize: '1.5rem', fontWeight: '600', color: '#1F2937', marginBottom: '1rem' }}>
            Questionnaire Not Found
          </h1>
          <p style={{ color: '#6B7280', marginBottom: '1.5rem' }}>
            This questionnaire link is invalid or has expired.
          </p>
        </div>
      </div>
    );
  }

  if (response?.status === 'submitted') {
    return (
      <div style={{ 
        minHeight: '100vh', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        background: '#F9FAFB'
      }}>
        <div style={{ textAlign: 'center', maxWidth: '500px', padding: '2rem' }}>
          <div style={{ 
            width: '64px', 
            height: '64px', 
            borderRadius: '50%', 
            background: '#10B981', 
            margin: '0 auto 1.5rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '2rem',
            color: '#ffffff'
          }}>
            ✓
          </div>
          <h1 style={{ fontSize: '1.5rem', fontWeight: '600', color: '#1F2937', marginBottom: '1rem' }}>
            Thank You!
          </h1>
          <p style={{ color: '#6B7280', marginBottom: '1.5rem' }}>
            Your questionnaire has been submitted successfully. We'll review your responses and get back to you soon.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ 
      minHeight: '100vh', 
      background: '#F9FAFB',
      padding: '2rem 1rem'
    }}>
      <div style={{ 
        maxWidth: '800px', 
        margin: '0 auto',
        background: '#ffffff',
        borderRadius: '8px',
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
        padding: '2rem'
      }}>
        <div style={{ marginBottom: '2rem' }}>
          <h1 style={{ 
            fontSize: '1.75rem', 
            fontWeight: '600', 
            color: '#1F2937', 
            marginBottom: '0.5rem' 
          }}>
            {questionnaire.title}
          </h1>
          {questionnaire.description && (
            <p style={{ color: '#6B7280', fontSize: '0.875rem', marginBottom: '1rem' }}>
              {questionnaire.description}
            </p>
          )}
          {response?.expiresAt && (
            <div style={{
              padding: '0.75rem',
              background: '#FEF3C7',
              borderRadius: '6px',
              borderLeft: '4px solid #F59E0B',
              fontSize: '0.875rem',
              color: '#92400E'
            }}>
              <strong>Deadline:</strong> {new Date(response.expiresAt).toLocaleString('en-US', {
                month: 'long',
                day: 'numeric',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
              })}
            </div>
          )}
        </div>

        <div style={{ marginBottom: '2rem' }}>
          {questionnaire.questions.map((question, index) => {
            const questionId = question._id?.toString() || index.toString();
            const answer = answers[questionId];

            return (
              <div key={index} style={{
                marginBottom: '2rem',
                padding: '1.5rem',
                border: '1px solid #E5E7EB',
                borderRadius: '6px',
                background: '#FAFAFA'
              }}>
                <label style={{
                  display: 'block',
                  marginBottom: '0.75rem',
                  fontWeight: '500',
                  color: '#1F2937',
                  fontSize: '1rem'
                }}>
                  {index + 1}. {question.question}
                  {question.required && <span style={{ color: '#EF4444', marginLeft: '0.25rem' }}>*</span>}
                </label>

                {question.type === 'text' && (
                  <input
                    type="text"
                    value={answer || ''}
                    onChange={(e) => handleAnswerChange(questionId, e.target.value)}
                    style={{
                      width: '100%',
                      padding: '0.625rem 0.875rem',
                      border: '1px solid #D1D5DB',
                      borderRadius: '6px',
                      fontSize: '0.875rem'
                    }}
                  />
                )}

                {question.type === 'textarea' && (
                  <textarea
                    value={answer || ''}
                    onChange={(e) => handleAnswerChange(questionId, e.target.value)}
                    rows={4}
                    style={{
                      width: '100%',
                      padding: '0.625rem 0.875rem',
                      border: '1px solid #D1D5DB',
                      borderRadius: '6px',
                      fontSize: '0.875rem',
                      fontFamily: 'inherit',
                      resize: 'vertical'
                    }}
                  />
                )}

                {question.type === 'multiple_choice' && (
                  <div>
                    {(question.options || []).map((option, optIndex) => (
                      <label key={optIndex} style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        marginBottom: '0.5rem',
                        cursor: 'pointer',
                        padding: '0.5rem',
                        borderRadius: '4px'
                      }}
                      onMouseEnter={(e) => e.target.style.background = '#F3F4F6'}
                      onMouseLeave={(e) => e.target.style.background = 'transparent'}
                      >
                        <input
                          type="radio"
                          name={questionId}
                          value={option.value}
                          checked={answer === option.value}
                          onChange={(e) => handleAnswerChange(questionId, e.target.value)}
                          style={{ cursor: 'pointer' }}
                        />
                        <span style={{ fontSize: '0.875rem', color: '#374151' }}>{option.label}</span>
                      </label>
                    ))}
                  </div>
                )}

                {question.type === 'checkbox' && (
                  <div>
                    {(question.options || []).map((option, optIndex) => (
                      <label key={optIndex} style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        marginBottom: '0.5rem',
                        cursor: 'pointer',
                        padding: '0.5rem',
                        borderRadius: '4px'
                      }}
                      onMouseEnter={(e) => e.target.style.background = '#F3F4F6'}
                      onMouseLeave={(e) => e.target.style.background = 'transparent'}
                      >
                        <input
                          type="checkbox"
                          value={option.value}
                          checked={(answer || []).includes(option.value)}
                          onChange={(e) => handleOptionChange(questionId, option.value, e.target.checked)}
                          style={{ cursor: 'pointer' }}
                        />
                        <span style={{ fontSize: '0.875rem', color: '#374151' }}>{option.label}</span>
                      </label>
                    ))}
                  </div>
                )}

                {question.type === 'yes_no' && (
                  <div style={{ display: 'flex', gap: '1rem' }}>
                    <label style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      cursor: 'pointer',
                      padding: '0.5rem 1rem',
                      borderRadius: '4px',
                      border: '1px solid #D1D5DB'
                    }}
                    onMouseEnter={(e) => e.target.style.background = '#F3F4F6'}
                    onMouseLeave={(e) => e.target.style.background = 'transparent'}
                    >
                      <input
                        type="radio"
                        name={questionId}
                        value="yes"
                        checked={answer === 'yes'}
                        onChange={(e) => handleAnswerChange(questionId, e.target.value)}
                        style={{ cursor: 'pointer' }}
                      />
                      <span>Yes</span>
                    </label>
                    <label style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      cursor: 'pointer',
                      padding: '0.5rem 1rem',
                      borderRadius: '4px',
                      border: '1px solid #D1D5DB'
                    }}
                    onMouseEnter={(e) => e.target.style.background = '#F3F4F6'}
                    onMouseLeave={(e) => e.target.style.background = 'transparent'}
                    >
                      <input
                        type="radio"
                        name={questionId}
                        value="no"
                        checked={answer === 'no'}
                        onChange={(e) => handleAnswerChange(questionId, e.target.value)}
                        style={{ cursor: 'pointer' }}
                      />
                      <span>No</span>
                    </label>
                  </div>
                )}

                {question.type === 'scale' && (
                  <div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                      <span style={{ fontSize: '0.875rem', color: '#6B7280' }}>{question.scaleMin || 1}</span>
                      <span style={{ fontSize: '0.875rem', color: '#6B7280' }}>{question.scaleMax || 10}</span>
                    </div>
                    <input
                      type="range"
                      min={question.scaleMin || 1}
                      max={question.scaleMax || 10}
                      value={answer || question.scaleMin || 1}
                      onChange={(e) => handleAnswerChange(questionId, parseInt(e.target.value))}
                      style={{ width: '100%' }}
                    />
                    <div style={{ textAlign: 'center', marginTop: '0.5rem', fontSize: '1.125rem', fontWeight: '600', color: '#1E40AF' }}>
                      {answer || question.scaleMin || 1}
                    </div>
                  </div>
                )}

                {question.type === 'file_upload' && (
                  <div>
                    <input
                      type="file"
                      onChange={async (e) => {
                        const file = e.target.files[0];
                        if (!file) return;
                        const maxSize = (question.maxFileSize || 10) * 1024 * 1024;
                        if (file.size > maxSize) {
                          toastr.error(`File too large. Max size: ${question.maxFileSize || 10}MB`);
                          return;
                        }
                        try {
                          handleAnswerChange(questionId, `Uploading ${file.name}...`);
                          const formData = new FormData();
                          formData.append('file', file);
                          const uploadRes = await fetch(`/api/questionnaires/upload-file/${token}`, {
                            method: 'POST',
                            body: formData
                          });
                          const data = await uploadRes.json();
                          if (uploadRes.ok && data.fileUrl) {
                            handleAnswerChange(questionId, data.fileUrl);
                            toastr.success(`${file.name} uploaded`);
                          } else {
                            handleAnswerChange(questionId, '');
                            toastr.error(data.error || 'Upload failed');
                          }
                        } catch (err) {
                          handleAnswerChange(questionId, '');
                          toastr.error('File upload failed. Please try again.');
                        }
                      }}
                      accept={question.acceptedFileTypes?.join(',') || '*'}
                      style={{
                        padding: '0.5rem',
                        border: '1px solid #D1D5DB',
                        borderRadius: '6px',
                        fontSize: '0.875rem'
                      }}
                    />
                    {answer && answer.startsWith('http') && (
                      <p style={{ fontSize: '0.75rem', color: '#059669', marginTop: '0.25rem' }}>
                        File uploaded successfully
                      </p>
                    )}
                    {answer && answer.startsWith('Uploading') && (
                      <p style={{ fontSize: '0.75rem', color: '#D97706', marginTop: '0.25rem' }}>
                        {answer}
                      </p>
                    )}
                    {question.maxFileSize && (
                      <p style={{ fontSize: '0.75rem', color: '#6B7280', marginTop: '0.25rem' }}>
                        Max file size: {question.maxFileSize}MB
                      </p>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div style={{ 
          display: 'flex', 
          justifyContent: 'flex-end', 
          gap: '0.75rem',
          paddingTop: '1.5rem',
          borderTop: '1px solid #E5E7EB'
        }}>
          <button
            onClick={handleSubmit}
            disabled={submitting}
            style={{
              padding: '0.75rem 2rem',
              background: submitting ? '#9CA3AF' : '#1E40AF',
              color: '#ffffff',
              border: 'none',
              borderRadius: '6px',
              cursor: submitting ? 'not-allowed' : 'pointer',
              fontWeight: '500',
              fontSize: '0.875rem'
            }}
            onMouseEnter={(e) => !submitting && (e.target.style.background = '#1E3A8A')}
            onMouseLeave={(e) => !submitting && (e.target.style.background = submitting ? '#9CA3AF' : '#1E40AF')}
          >
            {submitting ? 'Submitting...' : 'Submit Questionnaire'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default QuestionnaireResponse;


